import { useCallback, useMemo, useState } from 'react'

import { useController, useFormContext } from 'react-hook-form'

import CheckIcon from '@/assets/icons/icon-check.svg'
import ExpandMoreIcon from '@/assets/icons/icon-expand-more.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { Tooltip } from '@/components/ui/tooltip'
import { useRoles } from '@/modules/team/hooks/use-roles'
import { classNames } from '@/utils/helpers.ts'

import { RemovableBadge } from './RemovableBadge'

interface Props {
  selectedRoleIds: number[]
  onChange: (roles: number[]) => void
  disabled: boolean
  disabledReason?: string
  label?: string
  name: string
  className?: string
  showErrors?: boolean
  showSelectedRoles?: boolean
}

export const RolesSelector = ({
  onChange,
  selectedRoleIds,
  label,
  disabled,
  name,
  className,
  showErrors,
  disabledReason,
  showSelectedRoles = true,
}: Props) => {
  const { control } = useFormContext()
  const { field, fieldState } = useController({ name, control })
  const { data: roles } = useRoles()
  const [open, setOpen] = useState(false)

  const handleChange = useCallback(
    (roleId: number) => {
      const rolesToUpdate = selectedRoleIds.includes(roleId)
        ? selectedRoleIds.filter((id) => id !== roleId)
        : [...selectedRoleIds, roleId]

      onChange(rolesToUpdate)
    },
    [onChange, selectedRoleIds],
  )

  const selectedRoles = roles?.filter((role) =>
    selectedRoleIds.includes(role.id),
  )

  const selectedRolesText = useMemo(() => {
    if (selectedRoleIds.length === 0) {
      return 'No role selected'
    }

    if (selectedRoleIds.length === roles?.length) {
      return 'All roles'
    }

    return `${selectedRoleIds?.length} ${selectedRoleIds?.length > 1 ? 'roles' : 'role'} selected`
  }, [roles?.length, selectedRoleIds.length])

  const handleOpenChange = useCallback((isOpen: boolean) => {
    setOpen(isOpen)
  }, [])

  return (
    <div className="w-full">
      <div className={classNames('flex w-full flex-col gap-1.5', className)}>
        {label && (
          <label
            htmlFor={field.name}
            className="text-sm font-medium text-text-default"
          >
            {label}
          </label>
        )}
        <div className="relative flex flex-col items-center">
          <Popover
            modal
            open={open}
            onOpenChange={handleOpenChange}
          >
            <Tooltip
              contentClassName="z-100"
              content={disabledReason}
              className={classNames('w-full text-left')}
            >
              <PopoverTrigger
                asChild
                disabled={disabled}
              >
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="flex w-full justify-between"
                  disabled={disabled}
                >
                  <div className="flex gap-2 truncate font-normal">
                    <span>{selectedRolesText}</span>
                  </div>
                  <ExpandMoreIcon className="w-5" />
                </Button>
              </PopoverTrigger>
            </Tooltip>
            <PopoverContent className="z-100 w-[200px] p-0">
              <Command>
                <CommandList>
                  <CommandInput placeholder="Search Roles" />
                  <CommandEmpty>No roles found.</CommandEmpty>
                  <CommandSeparator />
                  <CommandGroup className=" max-h-44 overflow-auto">
                    {roles?.map((role) => {
                      const isSelected = selectedRoleIds.includes(role.id)

                      return (
                        <CommandItem
                          key={role.id}
                          onSelect={() => handleChange(role.id)}
                        >
                          <div
                            className={classNames(
                              'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary-500',
                              isSelected
                                ? 'bg-primary-500 text-white'
                                : 'opacity-50',
                            )}
                          >
                            {isSelected && <CheckIcon className="w-3" />}
                          </div>
                          {role.name}
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {showSelectedRoles && (
            <div className="mt-2 flex w-full flex-wrap gap-2">
              {selectedRoles?.map((role) => (
                <RemovableBadge
                  name={role.name}
                  value={role.id}
                  onRemove={handleChange}
                  key={role.id}
                  disabled={disabled}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {showErrors && fieldState.error && (
        <p className="mt-1 text-xs text-danger-default">
          {fieldState.error.message}
        </p>
      )}
    </div>
  )
}
